import { AuthService } from 'src/app/auth/auth.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { LanguageService, LanguageType } from 'src/app/shared/business/languages/language.service';
import { MediasService } from 'src/app/settings/medias/medias.service';
import { Resource } from 'src/app/shared/business/resources/resource';
import { ResourceService, ResourceType } from 'src/app/shared/business/resources/resource.service';
import { ResourceStatusEnum } from 'src/app/shared/business/resources/resource-status.enum';
import { ResourceTypeEnum } from 'src/app/shared/business/resources/resource-type.enum';
import { Sharing } from 'src/app/shared/sharing/entities/sharing';
import { SharingObjectType } from 'src/app/shared/sharing/entities/sharing-object-type.enum';
import { Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { filter } from 'rxjs/operators';

import { ModalBaseComponent } from '../../modal/modal-base-component';

@Component({
  selector: 'app-resource-media-edit-modal',
  templateUrl: './resource-media-edit-modal.component.html',
  styleUrls: ['./resource-media-edit-modal.component.scss'],
})
export class ResourceMediaEditModalComponent extends ModalBaseComponent<Resource> implements OnInit, OnDestroy {
  @Input()
  resource: Resource;

  @Input()
  options: any;

  public shareableTo: { type: string; id: number; title: string }[];
  public panel: boolean = false;
  public shareable: boolean = false;
  public resourceTypeImage = ResourceTypeEnum.image;

  public media: Resource;
  public oldShare: { type: string; id: number; title: string };

  public isMediaConverted: boolean;

  public resourceGroup: UntypedFormGroup;
  public languages: LanguageType[];
  public resourceTypes: ResourceType[];
  private subscriptions: Subscription = new Subscription();

  constructor(
    bsModalRef: BsModalRef,
    private formBuilder: UntypedFormBuilder,
    private mediaService: MediasService,
    private languageService: LanguageService,
    private resourceService: ResourceService,
    private toasterService: ToastrService,
    private translate: TranslateService,
    private authService: AuthService,
  ) {
    super(bsModalRef);
  }

  ngOnInit() {
    this.panel = this.options.panel || false;
    this.shareable = this.options.shareable || false;
    this.media = this.resource;
    this.languages = this.languageService.getCandidateLanguages();
    this.resourceTypes = this.resourceService.getTypesFor(this.media);
    this.shareableTo = this.options.shareableTo;
    if (this.panel && this.shareable && this.media.sharing) {
      this.oldShare = this.shareableTo.find(element => {
        return element.id === +this.media.sharing.recipientId && element.type === this.media.sharing.recipientType;
      });
    }

    if (!this.oldShare && this.shareableTo) {
      this.oldShare = this.shareableTo[0];
    }

    let formGroupObject: any = {
      title: [this.media.title, Validators.required],
      type: [this.media.type, Validators.required],
      language: [this.media.language, Validators.required],
    };

    if (this.oldShare) {
      formGroupObject = { ...formGroupObject, share: [this.oldShare, Validators.required] };
    }

    this.resourceGroup = this.formBuilder.group(formGroupObject);

    if (this.options.isNew) {
      this.resourceGroup.controls.title.markAsDirty();
    }

    this.isMediaConverted = this.media.status === ResourceStatusEnum.converted;

    // Handle type field state based on conversion status
    this.updateTypeFieldState();

    // Ensure we have the latest state in case conversion finished while modal was closed
    this.resourceService.get(this.media.id).subscribe(r => {
      this.media.status = r.status;
      this.media.duration = r.duration;
      this.isMediaConverted = r.status === ResourceStatusEnum.converted;
      this.updateTypeFieldState();
    });

    // Subscribe to conversion updates for this media and trigger polling if not converted yet
    if (!this.isMediaConverted) {
      this.subscriptions.add(
        this.resourceService.resourceUpdated.pipe(filter((r: Resource) => r && r.id === this.media.id)).subscribe(r => {
          this.media.status = r.status;
          this.media.duration = r.duration;
          this.isMediaConverted = r.status === ResourceStatusEnum.converted;
          this.updateTypeFieldState();
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private updateTypeFieldState() {
    const typeControl = this.resourceGroup.get('type');
    if (typeControl) {
      if (this.isMediaConverted) {
        typeControl.enable();
      } else {
        typeControl.disable();
      }
    }
  }

  onSubmit() {
    this.media.title = this.resourceGroup.value.title;
    // Only update type if the field is enabled (media is converted)
    if (this.isMediaConverted) {
      this.media.type = this.resourceGroup.value.type;
    }
    this.media.language = this.resourceGroup.value.language;
    this.media.from = 'upload';
    this.media.status = undefined;
    if (this.panel && this.options.shareable) {
      if (this.resourceGroup.value.share) {
        let sharingId = null;
        if (this.media.sharing) {
          sharingId = this.media.sharing.id;
        }

        let share: Partial<Sharing> = {
          objectId: this.media.id,
          objectOwnerId: this.media.ownerId,
          objectType: SharingObjectType.resource,
          recipientId: this.resourceGroup.value.share.id.toString(),
          recipientType: this.resourceGroup.value.share.type,
          creatorId: this.media.ownerId,
        };

        if (sharingId) {
          share = { ...share, id: sharingId };
        }

        this.mediaService.share(share).subscribe();
      }

      this.mediaService.putPanel(this.media, this.options.company.id).subscribe(() => {
        this.notifToaster();
        this.close();
      });
    } else {
      this.mediaService.put(this.media).subscribe(() => {
        this.notifToaster();
        this.close();
      });
    }
  }

  private notifToaster() {
    const content = this.translate.instant('MEDIAS.MODIFICATION_EFFECTUE_MESSAGE', {
      title: this.media.title,
      VAR_SELECT: this.media.type === 'image' ? 'image' : 'video',
    });

    this.toasterService.success(content, this.translate.instant('MEDIAS.MODIFICATION_EFFECTUE'));
  }
}
